import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useBreakpoint } from '@mds/shared/hooks/use-media-query';
import { useInView } from '@mds/shared/hooks/use-in-view';
import { Button } from '@mds/shared/ui/shadcn/button';

import { MediaCardRow, MediaCardSkeleton } from '@/components/card/media-card';
import { ErrorBlock } from '@/components/states';

/** How far below the screen the end of the list starts the next page on a phone. */
const PREFETCH_MARGIN = '0px 0px 600px 0px';

/**
 * The parts of a TanStack infinite query this needs. Structural, so any
 * `useInfiniteQuery` result fits whatever its page type.
 */
export interface InfiniteQueryState {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  error: unknown;
  fetchNextPage: () => unknown;
}

interface InfiniteCardListProps<T> {
  /** Every item loaded so far, across pages, in order. */
  items: T[];
  /** How many there are in all — from the response, not from `items`. */
  total: number;
  query: InfiniteQueryState;
  renderItem: (item: T) => ReactNode;
  /** What the list is of, for "37 events" and "12 more events loaded". */
  noun: { one: string; other: string };
  /** Matches the cards, so the placeholders look like what replaces them. */
  skeletonVariant?: 'bare' | 'framed';
}

/**
 * A grid of `MediaCard`s that loads the next page on demand — one behaviour
 * for every paged list on the site.
 *
 * - **Phone (below `lg`)** loads the next page by itself as the end of the
 *   list nears the screen. A thumb never has to stop and find a button.
 * - **Desktop (`lg` and up)** waits for a quiet "Load more" link, so the
 *   footer stays reachable and the visitor decides when to read on. Focus
 *   moves to the first new card, so a keyboard user carries on from there.
 *
 * Both keep the link (on a phone, an outlined button) for anyone who does not
 * scroll; both show placeholders where the next cards will land, announce
 * what arrived to screen readers, and swap the link for an alert with Retry if
 * a page fails — which also stops a phone from retrying by itself in a loop.
 *
 * The first page is the caller's: its loading, error and empty states sit in
 * place of this whole list, so they belong to the page around it.
 */
export function InfiniteCardList<T>({
  items,
  total,
  query,
  renderItem,
  noun,
  skeletonVariant = 'bare',
}: InfiniteCardListProps<T>) {
  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, error, fetchNextPage } = query;
  const desktop = useBreakpoint('lg');
  const listRef = useRef<HTMLDivElement>(null);

  // Phone: watch a marker after the last card. Off while a page is failing,
  // so a broken network does not turn into a request on every scroll.
  const [markerRef, nearEnd] = useInView<HTMLDivElement>({
    rootMargin: PREFETCH_MARGIN,
    enabled: !desktop && hasNextPage && !isFetchNextPageError,
  });
  useEffect(() => {
    // Runs again when a page finishes, so a marker still in view after a
    // short page carries straight on to the next.
    if (nearEnd && hasNextPage && !isFetchingNextPage && !isFetchNextPageError) fetchNextPage();
  }, [nearEnd, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  // Desktop: where focus goes once the page asked for arrives.
  const focusFrom = useRef<number | null>(null);
  const loadMore = () => {
    if (desktop) focusFrom.current = items.length;
    fetchNextPage();
  };

  // What arrived, for screen readers, and the hand-off of focus.
  const [announcement, setAnnouncement] = useState('');
  const previousCount = useRef(items.length);
  useEffect(() => {
    const before = previousCount.current;
    previousCount.current = items.length;
    if (before === 0 || items.length <= before) return;

    const added = items.length - before;
    setAnnouncement(
      `${added} more ${added === 1 ? noun.one : noun.other} loaded. Showing ${items.length} of ${total}.`,
    );

    if (focusFrom.current !== null) {
      const card = listRef.current?.querySelectorAll('article')[focusFrom.current];
      card?.querySelector<HTMLElement>('a, button')?.focus();
      focusFrom.current = null;
    }
  }, [items.length, total, noun]);

  const label = isFetchingNextPage ? 'Loading…' : `Load more ${noun.other}`;

  return (
    <>
      <div ref={listRef}>
        <MediaCardRow>
          {items.map(renderItem)}
          {isFetchingNextPage &&
            Array.from({ length: desktop ? 3 : 2 }, (_, i) => (
              <MediaCardSkeleton key={`skeleton-${i}`} variant={skeletonVariant} />
            ))}
        </MediaCardRow>
      </div>

      {hasNextPage && !isFetchNextPageError && !desktop && (
        <div ref={markerRef} aria-hidden="true" />
      )}

      {/* `mt-8 sm:mt-12`: the band a row of cards leaves under itself, which
          `MediaCardRow` cancels after its last row, put back above the link. */}
      <div className="mt-8 flex flex-col items-center sm:mt-12">
        {isFetchNextPageError ? (
          <div className="w-full">
            <ErrorBlock
              title={`We couldn't load more ${noun.other}`}
              error={error}
              onRetry={() => void fetchNextPage()}
            />
          </div>
        ) : hasNextPage ? (
          <>
            {desktop ? (
              // A quiet text link, grey at rest and brand green on hover and
              // focus — present, but not competing with the cards.
              <button
                type="button"
                onClick={loadMore}
                disabled={isFetchingNextPage}
                className="rounded-sm px-0.5 py-1 text-sm font-medium text-ink-muted underline-offset-4 transition-colors duration-(--duration-fast) hover:text-brand-text hover:underline focus-visible:text-brand-text focus-visible:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-progress disabled:text-ink-muted disabled:no-underline motion-reduce:transition-none"
              >
                {label}
              </button>
            ) : (
              <Button variant="outline" size="lg" onClick={loadMore} disabled={isFetchingNextPage}>
                {label}
              </Button>
            )}
            {/* Its caption: on desktop a step smaller and tight under the link. */}
            <span
              className={
                desktop ? 'text-2xs leading-snug text-ink-muted' : 'mt-2 text-sm text-ink-muted'
              }
            >
              {items.length} of {total}
            </span>
          </>
        ) : (
          <span className="text-xs text-ink-muted">You've reached the end</span>
        )}
      </div>

      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
    </>
  );
}
